/**
 * The waterfall: the stages of one AI call, drawn to scale.
 *
 * Two sizes of the same picture. The list shows a one-line strip per call so a
 * column of them reads as "where do these usually stop"; the detail shows every
 * stage as its own row, with what it found and — for the one that broke — why.
 *
 * Positioned by `startMs`, not stacked end to end: a warning is an instant
 * inside a stage, and stacking would give it width it never had.
 */
import { useState } from 'react';

import { STEP_COLOR, fmtMs, stepLabel, type ObsStep } from '../../types/ai-observability.types';

/** The call's full span — the last stage's end, or 1 so a bar never divides by zero. */
function spanOf(steps: ObsStep[]): number {
  return Math.max(1, ...steps.map((s) => s.startMs + (s.durationMs ?? 0)));
}

/** A strip of the stages, for scanning a column of calls. */
export function MiniWaterfall({ steps }: { steps: ObsStep[] }) {
  const stages = steps.filter((s) => s.status !== 'WARN');
  const warnings = steps.filter((s) => s.status === 'WARN');
  const span = spanOf(steps);
  return (
    <div style={{ position: 'relative', height: 10, width: 150, background: 'var(--obs-sunken)',
      borderRadius: 3, overflow: 'hidden' }}
      title={stages.map((s) => `${stepLabel(s.name)} ${fmtMs(s.durationMs)}${
        s.status === 'FAILED' ? ' — FAILED' : ''}`).join('\n')}>
      {stages.map((s, i) => (
        <div key={i} style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `${(s.startMs / span) * 100}%`,
          // At least a sliver, so a 2 ms stage is still visible as a stage.
          width: `max(2px, ${((s.durationMs ?? 0) / span) * 100}%)`,
          background: STEP_COLOR[s.status], opacity: s.status === 'FAILED' ? 1 : 0.75,
          borderRight: '1px solid var(--obs-card, #fff)',
        }} />
      ))}
      {warnings.map((w, i) => (
        <div key={`w${i}`} style={{
          position: 'absolute', top: 0, bottom: 0, width: 2,
          left: `${(w.startMs / span) * 100}%`, background: STEP_COLOR.WARN,
        }} />
      ))}
    </div>
  );
}

/** The facts a stage recorded, as a compact key/value grid. */
function Attrs({ attrs }: { attrs: Record<string, unknown> }) {
  const entries = Object.entries(attrs).filter(([k, v]) =>
    v !== null && v !== undefined && k !== 'chunks' && k !== 'fields');
  if (!entries.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
      gap: '6px 16px', marginTop: 8 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10.5, color: 'var(--obs-ink-4)' }}>{k}</div>
          <div className="obs-mono" style={{ fontSize: 12, color: 'var(--obs-ink-2)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One stage: its bar, and — when opened — what it found. */
function StageRow({ step, span, first }: { step: ObsStep; span: number; first: boolean }) {
  // The broken stage and the warnings open themselves: they are the reason the
  // page was opened, and making someone click to find the error is backwards.
  const [open, setOpen] = useState(step.status !== 'OK');
  const color = STEP_COLOR[step.status];
  const isWarn = step.status === 'WARN';

  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--obs-line-soft)', padding: '9px 0' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        all: 'unset', cursor: 'pointer', display: 'grid', width: '100%',
        gridTemplateColumns: '14px minmax(160px, 220px) 1fr 70px', gap: 10, alignItems: 'center',
      }} aria-expanded={open}>
        <span style={{ width: 9, height: 9, borderRadius: isWarn ? 2 : '50%', background: color,
          transform: isWarn ? 'rotate(45deg)' : undefined }} />
        <span style={{ fontSize: 13, fontWeight: step.status === 'FAILED' ? 600 : 500,
          color: step.status === 'FAILED' ? '#B91C1C' : 'var(--obs-ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {stepLabel(step.name)}
        </span>
        <span style={{ position: 'relative', height: 8, background: 'var(--obs-sunken)', borderRadius: 3 }}>
          <span style={{
            position: 'absolute', top: 0, bottom: 0, borderRadius: 3, background: color,
            left: `${(step.startMs / span) * 100}%`,
            width: isWarn ? 3 : `max(3px, ${((step.durationMs ?? 0) / span) * 100}%)`,
          }} />
        </span>
        <span className="num" style={{ fontSize: 12, textAlign: 'right', color: 'var(--obs-ink-3)',
          fontVariantNumeric: 'tabular-nums' }}>
          {isWarn ? 'warning' : fmtMs(step.durationMs)}
        </span>
      </button>

      {open && (
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          {step.error && (
            <div style={{
              fontSize: 12.5, lineHeight: 1.55, padding: '8px 11px', borderRadius: 6, marginTop: 4,
              background: step.status === 'FAILED' ? '#FEF2F2' : '#FFFBEB',
              border: `1px solid ${step.status === 'FAILED' ? '#FECACA' : '#FDE68A'}`,
              color: step.status === 'FAILED' ? '#991B1B' : '#92400E',
            }}>
              {step.status === 'FAILED' && (
                <div className="obs-mono" style={{ fontSize: 11, marginBottom: 3, opacity: 0.85 }}>
                  {step.error.type}{step.error.status ? ` · ${step.error.status}` : ''}
                  {step.error.code ? ` · ${step.error.code}` : ''}
                </div>
              )}
              {step.error.message}
            </div>
          )}
          {step.attrs && <Attrs attrs={step.attrs} />}
        </div>
      )}
    </div>
  );
}

/** Every stage as a row, in order, to scale. */
export function Waterfall({ steps }: { steps: ObsStep[] }) {
  const span = spanOf(steps);
  return (
    <div>
      {steps.map((s, i) => <StageRow key={i} step={s} span={span} first={i === 0} />)}
    </div>
  );
}
